import React, { useState } from 'react';
import {
  Button, DataTable, IdCell, KV, Modal, PageHead, Textarea, dateTimeStr, inr, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { approvalApi } from '../api';
import { mediaUrl } from '../api/media';
import { useToast } from '../context/ToastContext';

const TABS = [
  { value: 'pending', label: 'Awaiting review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

/**
 * Listings a seller has submitted. Nothing reaches the buyer-facing catalogue
 * until it is approved here, and a seller editing an approved listing sends it
 * back to this queue.
 */
const Approvals = () => {
  const toast = useToast();
  const list = useList(approvalApi.list, { status: 'pending' });

  const [review, setReview] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const decide = async decision => {
    if (decision === 'rejected' && !reason.trim()) {
      return toast.error('Tell the seller what to fix');
    }

    setBusy(true);
    try {
      const res = await approvalApi.review(review._id, { decision, reason });
      toast.success(res.message);
      setReview(null);
      setReason('');
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const columns = [
    {
      key: 'product',
      label: 'Listing',
      render: row => <IdCell image={mediaUrl(row.thumbnail)} name={row.name} sub={row.sku || '—'} />,
    },
    { key: 'seller', label: 'Seller', render: row => <IdCell name={row.manufacturer?.name} sub={row.manufacturer?.code} /> },
    {
      key: 'categories',
      label: 'Categories',
      render: row => (
        <span className="text-sm">
          {(row.categories || []).length
            ? row.categories.map(c => c.name || '—').join(', ')
            : row.category?.name || '—'}
        </span>
      ),
    },
    { key: 'price', label: 'Price', align: 'right', render: row => inr(row.basePrice) },
    {
      key: 'submitted',
      label: 'Submitted',
      render: row => <span className="text-sm text-muted">{dateTimeStr(row.approval?.submittedAt)}</span>,
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => (
        <Button size="sm" onClick={() => { setReview(row); setReason(''); }}>
          {row.approval?.status === 'pending' ? 'Review' : 'View'}
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Listing approvals"
        subtitle="Seller submissions stay out of the catalogue until you approve them."
      />

      <div className="filters">
        {TABS.map(tab => (
          <Button
            key={tab.value}
            size="sm"
            variant={list.filters.status === tab.value ? 'primary' : 'ghost'}
            onClick={() => list.setFilter('status', tab.value)}
          >
            {tab.label}
          </Button>
        ))}
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} listings` : ''}</span>
      </div>

      <DataTable columns={columns} rows={list.rows} loading={list.loading} meta={list.meta} onPage={list.setPage} />

      <Modal
        open={!!review}
        size="lg"
        title={review?.name}
        subtitle={review?.manufacturer?.name}
        onClose={() => setReview(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setReview(null)}>Close</Button>
            {review?.approval?.status !== 'rejected' && (
              <Button variant="danger" loading={busy} onClick={() => decide('rejected')}>Reject</Button>
            )}
            {review?.approval?.status !== 'approved' && (
              <Button loading={busy} onClick={() => decide('approved')}>Approve & publish</Button>
            )}
          </>
        }
      >
        {review && (
          <>
            {(review.images || []).length > 0 && (
              <div className="ticket-files">
                {review.images.map(url => (
                  <a key={url} href={mediaUrl(url)} target="_blank" rel="noreferrer">
                    <img src={mediaUrl(url)} alt={review.name} />
                  </a>
                ))}
              </div>
            )}

            <KV
              items={[
                { label: 'Seller', value: review.manufacturer?.name },
                { label: 'SKU', value: review.sku },
                {
                  label: 'Categories',
                  value: (review.categories || []).map(c => c.name).join(', ') || review.category?.name,
                },
                { label: 'Sold by', value: review.unit },
                { label: 'Price', value: inr(review.basePrice) },
                { label: 'MOQ', value: `${num(review.moq || 1)} ${review.unit}` },
                { label: 'Weight', value: review.weightKg ? `${review.weightKg} kg` : '—' },
                { label: 'Status', value: review.approval?.status },
                ...(review.approval?.rejectionReason
                  ? [{ label: 'Last rejection', value: review.approval.rejectionReason }]
                  : []),
              ]}
            />

            {review.shortDescription && <div className="ticket-body">{review.shortDescription}</div>}
            {review.description && <div className="ticket-body">{review.description}</div>}

            {review.approval?.status !== 'approved' && (
              <Textarea
                label="Reason (required to reject)"
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={3}
                placeholder="What the seller needs to change before this can go live."
              />
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default Approvals;
